<template>

    <div class="flex flex-column xl:flex-row lg:flex-row md:flex-row px-3 py-2">
        <div class="flex flex-row align-items-center gap-2">
            <Button label="Сбросить" icon="pi pi-filter" size="small" @click="resetFilter" outlined severity="info"/>
            <Button label="Создать" icon="pi pi-plus" size="small" @click="createPopup=true" severity="info"/>
        </div>
    </div>

    <DataTable v-if='partners' :value="partners" responsiveLayout="scroll"
        lazy paginator :rows="10" :rowsPerPageOptions="[5, 10, 20, 50, 100]" :totalRecords="totalRecords" @filter="onFilter($event)"
        @page="onPage($event)" @sort="onSort($event)" :loading="loading" 
        v-model:filters="filters" filterDisplay="menu" :globalFilterFields="['id', 'user_id', 'code', 'transfers', 'users', 'created_at']">

        <Column field="id" filterField="id" filterMatchMode="contains" :showFilterMenu="true" :showFilterMatchModes="false">
            <template #header>
                <span class="text-xs font-medium">ID</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()"
                    class="p-column-filter w-12rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <span class="text-sm flex flex-row" @click="copy(data.id)">
                    <span class="flex">{{get_short_id(data.id)}}</span>
                    <span class="flex pi pi-copy ml-2 text-500 text-xs cursor-pointer"></span>
                </span>
            </template>
        </Column>

        <Column field="user_id" filterField="user_id" filterMatchMode="contains" :showFilterMenu="true" :showFilterMatchModes="false">
            <template #header>
                <span class="text-xs font-medium">Пользователь</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <span v-if="data.user && data.user.name" class="text-sm flex flex-row gap-2">
                    <span class="flex text-xs">{{ data.user.name}} </span>
                    <span class="pi pi-external-link text-xs flex cursor-pointer text-primary" @click="detail_page(data.user_id, 'user', true)"></span>
                    <span class="flex pi pi-copy text-xs cursor-pointer text-primary" @click="copy(data.id)"></span>
                </span>
            </template>
        </Column>

        <Column field="code" filterField="code" filterMatchMode="contains" :showFilterMenu="true" :showFilterMatchModes="false">
            <template #header>
                <span class="text-xs font-medium">Код</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <span v-if="data.code" class="text-sm flex flex-row gap-2">
                    <span class="flex text-xs">{{ data.code }} </span>
                    <span class="flex pi pi-copy text-xs cursor-pointer text-primary" @click="copy(data.code)"></span>
                    <span class="flex pi pi-link text-xs cursor-pointer text-primary" @click="copyHref(data.code)"></span>
                </span>
            </template>
        </Column>

        <Column field="user.login">
            <template #header>
                <span class="text-xs font-medium">Логин</span>
            </template>
            <template #body="{ data }">
                <p v-if="data.user && data.user.login" class="text-xs white-space-nowrap">
                    {{ data.user.login}}
                </p>
            </template>
        </Column>

        <Column field="user.wallet.id" filterMatchMode="startsWith">
            <template #header>
                <span class="text-xs font-medium">Wallet</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
                <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()"
                    class="p-column-filter w-12rem" placeholder="Найти" />
            </template>
            <template #body="{ data }">
                <span v-if="data.wallet.id" class="text-sm flex flex-row gap-2" @click="copy(data.wallet.id)">
                    <span class="flex">{{get_short_id(data.wallet.id)}}</span>
                    <span class="pi pi-external-link text-xs flex cursor-pointer text-primary" @click="detail_page(data.wallet.id, 'wallet', true)"></span>
                    <span class="flex pi pi-copy ml-2 text-500 text-xs cursor-pointer text-primary"></span>
                </span>
            </template>
        </Column>

        <Column field="wallet.balance">
            <template #header>
                <span class="text-xs font-medium">Баланс</span>
            </template>
            <template #body="{ data }">
                <span v-if="data.wallet.balance">
                    {{ get_format_amount(data.wallet.balance, 'usdt') }}
                </span>
            </template>
        </Column>

        <Column field="percent">
            <template #header>
                <span class="text-xs font-medium">Процент</span>
            </template>
            <template #body="{ data }">
                <span v-if="data.wallet.balance">
                    {{ data.percent }}
                </span>
            </template>
        </Column>

        <Column field="count.transfers" sortable>
            <template #header>
                <span class="text-xs font-medium">Переводов</span>
            </template>
            <template #body="slotProps">
                <span v-if="slotProps.data.count && slotProps.data.count.transfers" class="flex">
                    {{ (slotProps.data.count.transfers) ? slotProps.data.count.transfers : 0 }}
                </span>
            </template>
        </Column>

        <Column field="count.users" sortable>
            <template #header>
                <span class="text-xs font-medium">Приглашенных</span>
            </template>
            <template #body="slotProps">
                <span v-if="slotProps.data.count && slotProps.data.count.users" class="flex">
                    {{ (slotProps.data.count.users) ? slotProps.data.count.users : 0 }}
                </span>
            </template>
        </Column>

        <Column field="created_at" sortable filterField="date_created" dataType="date">
            <template #header>
                <span class="text-xs font-medium">Создан</span>
            </template>
            <template #body="slotProps">
                <span class="text-sm flex flex-row">
                    {{ formatDate(slotProps.data.created_at) }}
                </span>
            </template>
        </Column>

        <Column field="id">
            <template #body="slotProps">
                <Button label="Подробно" @click="detail_page(slotProps.data.id, 'partner', true)" size="small" icon="pi pi-external-link" icon-pos="right" severity="info"/>
            </template>
        </Column>

        <template #footer>
            <span class="text-sm mr-3">Всего записей: {{ totalRecords }}</span>
            <span class="text-sm mr-3">Пользователей на стр.: {{ totalUsers }}</span>
            <span class="text-sm mr-3">Переводов на стр.: {{ totalTransfers }}</span>
        </template>
    </DataTable>
    <div v-else class="flex flex-column align-items-center justify-content-center mt-3 px-2">
        <Skeleton height="2rem" />
    </div>

    <Dialog v-model:visible="createPopup" :modal="true" :closeOnEscape="true" :dismissableMask="true" :style="{ width: '30vw' }">
        <template #header>
            <span class="text-sm font-medium">Создать партнера по коду</span>
        </template>
        <div class="flex flex-column gap-2">
            <InputText v-model="code" placeholder="Введите код" />
        </div>
        <template #footer>
            <Button label="Создать" icon="pi pi-check" @click="CreateByCode()" :loading="loading" :disabled="!code || loading"/>
        </template>
    </Dialog>
</template>

<script>
import Connect from '@/assets/js/Connect'
import Helper from '@/assets/js/Helper'
export default {
    data() {
        return {
            partners: null,
            loading: false,
            lazyParams: {
                first: 0,
                rows: 10,
                sortField: 'created_at',
                sortOrder: -1,
                filters: {}
            },
            totalRecords: 10,
            totalUsers: 0,
            totalTransfers: 0,
            filters: {
                'id': { value: null, matchMode: 'contains' },
                'user_id': { value: null, matchMode: 'contains' },
                'code': { value: null, matchMode: 'contains' },
            },
            createPopup: false,
            code: '',
        }
    },
    methods: {
        isUserInGroup: function (type) { 
            return this.helper.isUserInGroup(type, this.$store.getters.StateGroups)
        },
        onPage(event) {
            this.lazyParams = event
            this.loadLazyData()
        },
        onSort(event) {
            this.lazyParams = event;
            this.loadLazyData();
        },
        onFilter() {
            this.lazyParams.filters = this.filters
            this.loadLazyData();
        },
        async resetFilter() {
            this.filters = {}
            this.lazyParams.filters = this.filters
            await this.loadLazyData()
        },
        async loadLazyData() {
            this.loading = true
            window.scrollTo({ top: 0, behavior: 'smooth' }) //скролл вверх

            const limit = (this.lazyParams.rows) ? this.lazyParams.rows : 10
            const skip = (this.lazyParams.first) ? this.lazyParams.first : 0
            const filter = (this.lazyParams.filters) ? this.lazyParams.filters : {}

            let sort = {created_at: -1} //default sort
            if(this.lazyParams.sortField && this.lazyParams.sortOrder){
                sort = {}
                sort[this.lazyParams.sortField] = (this.lazyParams.sortOrder == 1) ? 1 : -1
            }

            const partners = await this.connect.partner_list(limit, skip, filter, sort).then(res => res)
            if (partners.result && partners.data.length > 0) {
                this.partners = partners.data
                this.totalRecords = partners.total
                
                for (let i = 0; i < this.partners.length; i++) {
                    this.totalUsers += (this.partners[i].users) ? this.partners[i].users : 0
                    this.totalTransfers += (this.partners[i].transfers) ? this.partners[i].transfers : 0
                }
            }
            this.loading = false
        },
        get_format_amount(value, currency) {
            if(!value) return 0
            return this.helper.format_amount(value, currency)
        },
        get_short_id(value){
            return this.helper.get_short_id(value)
        },
        detail_page(id, type='user', new_tab = false) {
            if(new_tab){
                const routeData = this.$router.resolve({path: '/admin/'+type+'/' + id});
                window.open(routeData.href, '_blank');
            } else  this.$router.push({ path: '/admin/'+type+'/' + id })
        },
        copy(text){
            this.helper.copy_clipboard(text)
            this.$toast.add({ severity: 'info', summary: 'Скопировано', detail: text, life: 1000 })
        },
        copyHref(text){
            const domain = this.connect.domain_url
            this.helper.copy_clipboard(`${domain}?ref=${text}`)
            this.$toast.add({ severity: 'info', summary: 'Скопировано', detail: `${domain}?ref=${text}`, life: 1000 })
        },
        formatDate(value) {
            return this.helper.format_date(value)
        },
        async CreateByCode(){
            this.loading = true
            const result = await this.connect.CreateRefLinkByCode(this.code).then(res => res)
            if (result.result) {
                this.$toast.add({ severity: 'success', summary: 'Успех', detail: 'Партнер создан', life: 1000 })
                await this.loadLazyData().then(res => res)
                this.createPopup = false
                this.code = ''
            } else {
                if(result.errors && result.errors[0]) this.$toast.add({ severity: 'error', summary: 'Ошибка', detail: result.errors[0], life: 1000 })
            }
            this.loading = false
        }
    },
    async beforeCreate() {
        this.connect = new Connect()
        this.helper = new Helper()
    },
    mounted() {
        this.lazyParams = {
            first: 0,
            rows: 10,
            sortField: null,
            sortOrder: null,
            filters: this.filters,
            // sort: 
        }
        
        this.lazyParams.filters = this.filters

        // получить get параметр user_id
        const user_id = (this.$route.query.user_id) ? this.$route.query.user_id : null
        if(user_id) this.filters.user_id = {value: user_id, matchMode: 'contains'}

        this.loadLazyData()
    }
}
</script>